import React from 'react';
import { useRecoilValue } from 'recoil';

import { ModeIcon } from '@/components/mode-icon';
import { getSequenceDescription, getSequenceTitle } from '@/components/modules/option/bar-sequence/utils/strings';
import { RouteTooltip } from '@/components/modules/route/tooltip';
import { Tooltip } from '@/components/tooltip';
import { DATA_IMAGES } from '@/lib/constants/styles';
import { OptionModel } from '@/lib/models/option/types';
import { SegmentLinkModel } from '@/lib/models/segment/types';
import { locationsState } from '@/state/locations';
import { SegmentModeType } from '@/types/api-types';
import { getTransportColor } from '@/utils/helpers/get-transport-color';
import { tv } from '@/utils/styles';

export interface RouteBarMiniProps {
  end?: SegmentLinkModel;
  fullWidth?: boolean;
  mainOptions: OptionModel[];
  primaryMode: SegmentModeType;
  start?: SegmentLinkModel;
}

export const STRIPED_STYLE = { backgroundImage: `url('${DATA_IMAGES.STRIPED_LIGHT}')` };

const RouteBarMini = ({ mainOptions, start, end, primaryMode, fullWidth = false }: RouteBarMiniProps) => {
  const locations = useRecoilValue(locationsState);

  const color = getTransportColor(primaryMode);

  // we ignore transfers when rendering the sequence bar
  const filteredSegments = mainOptions[0].segments.filter((s) => s.routeType === primaryMode);

  const { base, transfer, row, iconWrapper } = styles({ fullWidth });
  return (
    <div className={base()}>
      {start && (
        <Tooltip
          content={<RouteTooltip title={getSequenceTitle(start, locations)} durationSeconds={start.durationSeconds} />}
          followCursor="horizontal"
        >
          <span className={transfer({ isSequenceStart: true })} style={STRIPED_STYLE} />
        </Tooltip>
      )}
      <div className={row()} style={{ '--row-background': color } as React.CSSProperties}>
        {filteredSegments.map((segment) => {
          const title = getSequenceTitle(segment, locations);
          const description = getSequenceDescription(segment, locations);

          return (
            <Tooltip
              key={segment.id}
              content={
                <RouteTooltip title={title} durationSeconds={segment.durationSeconds} description={description} />
              }
              followCursor="horizontal"
            >
              <div className={iconWrapper()}>
                <ModeIcon mode={segment.routeType} size="sm" />
              </div>
            </Tooltip>
          );
        })}
      </div>
      {end && (
        <Tooltip
          content={<RouteTooltip title={getSequenceTitle(end, locations)} durationSeconds={end.durationSeconds} />}
          followCursor="horizontal"
        >
          <span className={transfer({ isSequenceEnd: true })} style={STRIPED_STYLE} />
        </Tooltip>
      )}
    </div>
  );
};

const styles = tv({
  slots: {
    base: 'flex cursor-pointer items-center',
    transfer:
      'z-2 -mr-3 block h-5 w-5 !bg-grey-400 bg-[length:4px] bg-repeat opacity-40 last:-ml-3 last:mr-0 hover:opacity-80',
    row: "relative z-3 mx-1 grid w-20 auto-cols-fr grid-flow-col rounded-[10px] bg-white after:absolute after:bottom-0 after:left-0 after:right-0 after:top-0 after:z-1 after:rounded-[10px] after:bg-[var(--row-background)] after:opacity-60 after:content-['']",
    iconWrapper: 'z-4 rounded-[10px] hover:z-7 hover:bg-[var(--row-background)]',
  },
  variants: {
    fullWidth: {
      true: {
        row: 'w-full',
      },
    },
    isSequenceStart: {
      true: {
        transfer: 'rounded-l-[10px]',
      },
    },
    isSequenceEnd: {
      true: {
        transfer: 'rounded-r-[10px]',
      },
    },
  },
});

export { RouteBarMini };
